import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import api from "@/lib/api";

// IMPORTANT (iOS Safari): navigator.credentials.create()/get() must run inside the
// same user-activation gesture. Do NOT put prompt()/alert()/confirm() before these
// calls — Safari consumes the activation and throws NotAllowedError. Fetching the
// server options is allowed; a blocking dialog is not.
export async function registerPasskey(name) {
  const { data: options } = await api.post("/auth/passkey/register/options");
  const cred = await startRegistration({ optionsJSON: options });
  await api.post("/auth/passkey/register/verify", { credential: cred, name });
}

export async function loginPasskey(email) {
  const { data } = await api.post("/auth/passkey/login/options", { email });
  const cred = await startAuthentication({ optionsJSON: data.options });
  const { data: user } = await api.post("/auth/passkey/login/verify", { challenge_id: data.challenge_id, credential: cred });
  return user;
}

// Human-friendly messages per WebAuthn DOMException name (never a raw technical error).
const FRIENDLY = {
  NotAllowedError: "Passkey request was cancelled or timed out. Tap Add and complete Face ID / Touch ID right away.",
  SecurityError: "Passkeys aren't available on this address. Please use https://duruvault.com and try again.",
  InvalidStateError: "A passkey for this account already exists on this device. Try signing in with it instead.",
  NotSupportedError: "This device or browser doesn't support passkeys.",
  AbortError: "Passkey request was cancelled. Please try again.",
  TypeError: "Passkey setup couldn't be completed. Please try again.",
};

// Reports a client-side WebAuthn failure to the server for secure diagnostics
// (name + safe message only — no secrets) and returns a friendly message.
export async function reportPasskeyError(phase, err) {
  const name = err?.name || "Error";
  try {
    await api.post("/auth/passkey/client-error", {
      phase,
      name,
      message: String(err?.message || "").slice(0, 300),
    });
  } catch (_) { /* diagnostics only — never block the user */ }
  return FRIENDLY[name] || "Passkey setup couldn't be completed. Please try again.";
}

// Sensible default passkey label (no blocking prompt, so the Safari gesture survives).
export function defaultPasskeyName() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Macintosh|Mac OS X/.test(ua)) return "Mac";
  if (/Android/.test(ua)) return "Android device";
  if (/Windows/.test(ua)) return "Windows device";
  return "Passkey";
}
